import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  IconButton,
} from "@mui/material";

//狀態管理
import { observer } from "mobx-react-lite";
import commonStore from "../../../Store/marco/commonStore.js";
import emissionViewStore from "../../../Store/marco/emissionViewStore";
import { getConstByStandard } from "../../utils/getConstFileByStandard";

//icons
import CloseIcon from "@mui/icons-material/Close";
//api
import { dataEntryAPI } from "../../API/dataEntryAPI";
//翻譯
import { useTranslation } from "react-i18next";
//hooks
import { CustomSelector } from "../CustomSelector.js";

//main
const DataEntrySetUnitModal = observer(() => {
  const { t } = useTranslation("ActivityMain");

  const [unit, setUnit] = useState("");
  const [dataFormat, setDataFormat] = useState("");
  const [unitOptions, setUnitOptions] = useState([]);
  //檢查按鈕是否可以點擊
  const [isSubmitting, setIsSubmitting] = useState(false);
  //儲存錯誤訊息
  const [errors, setErrors] = useState({ unit: false, dataFormat: false });

  const store = emissionViewStore;
  const CONST = getConstByStandard(store.standardId);
  const category = CONST.category;
  const scope = category[store.selectedTab].split("_")[0]; //cat1
  const cat = category[store.selectedTab].substring(3); // 从索引 3 开始截取到末尾，1_1

  //取得單位下拉式清單資料
  const getOptions = async () => {
    try {
      const data = await dataEntryAPI.unitOptionDataEntryAPI(
        store.selectedDataEntry.id,
        scope,
        cat,
      ); // 使用引入的 API 函數
      console.log("取得單位下拉式清單資料:", data);
      setUnitOptions(data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  //如果showDataEntrySetUnitModal開啟就去拿下拉式選單的選項
  //如果關閉的話就把unit跟format清空
  useEffect(() => {
    if (store.showDataEntrySetUnitModal) {
      getOptions();
      setDataFormat(store.selectedDataEntry.dataTypeId || "");
      setUnit(store.selectedDataEntry.dataUnitId || "");
    } else {
      setUnit("");
      setDataFormat("");
      setErrors({
        unit: false,
        dataFormat: false,
      });
    }
  }, [store.showDataEntrySetUnitModal]);

  if (!store.selectedDataEntry) return null;

  const timeFormatOptions = [
    { value: t("Year"), dataTypeId: 2 },
    { value: t("Month"), dataTypeId: 1 },
    { value: t("Day"), dataTypeId: 3 },
    { value: t("Interval"), dataTypeId: 4 },
  ];

  //驗證功能&之後設定Unit的方法
  const handleSave = async () => {
    // 如果正在提交中，直接返回
    if (isSubmitting) return;
    setIsSubmitting(true); // 開始提交時設置標誌

    const newErrors = {
      unit: !unit,
      dataFormat: !dataFormat,
    };
    setErrors(newErrors);

    if (!unit || !dataFormat) {
      setIsSubmitting(false);
      return; // 有錯誤就直接 return，不繼續執行
    }

    try {
      //////// 設定單位
      const settingSuccess = await store.setDE(
        dataFormat, //dataTypeId
        unit, //dataUnitId
      );

      if (!settingSuccess) return;

      // 重新獲取排放活動清單
      await store.getDEListData({ keepPage: true });

      // 只有當兩個操作都成功時才關閉對話框
      store._handleCloseDataEntrySetUnitModal();
      console.log("Setting:", { unit, dataFormat });
    } catch (error) {
      console.error("Error in setting operation", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={store.showDataEntrySetUnitModal}
      onClose={() => store._handleCloseDataEntrySetUnitModal()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <IconButton
          onClick={() => store._handleCloseDataEntrySetUnitModal()}
          sx={{
            position: "absolute",
            right: 8,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {t("Data Setting For") + " " + store.selectedDataEntry.itemName}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          {/* 使用單位選項 */}
          <CustomSelector
            label={t("Choose Unit")}
            value={unit} // 這裡的 unit 現在應該存儲 unitId
            handleChange={(e, selectedOption) => {
              setUnit(selectedOption.unitId); // 存儲 ID 而非文字
              setErrors((prev) => ({ ...prev, unit: false }));
            }}
            options={unitOptions}
            valueKey="unitId"
            labelKey="unitName"
            error={errors.unit}
            placeholder={t("Unset")}
            disabled={store.selectedDataEntry.hasDirectApiEntryEntity}
          />

          {/* 使用時間格式選項 */}
          <CustomSelector
            label={t("Choose Time Format")}
            value={dataFormat} // 這裡的 format 現在應該存儲 dataTypeId
            handleChange={(e, selectedOption) => {
              setDataFormat(selectedOption.dataTypeId); // 存儲 ID 而非文字
              setErrors((prev) => ({ ...prev, dataFormat: false }));
            }}
            options={timeFormatOptions}
            // options={cat == "2_1" ? timeFormatOptionsCat2_1 : timeFormatOptions} //先暫時限定2_1才可以選日期和區間
            valueKey="dataTypeId"
            labelKey="value"
            error={errors.dataFormat}
            disabled={
              !store.selectedDataEntry.allowModifyUnit ||
              store.selectedDataEntry.hasDirectApiEntryEntity
            }
            showTooltip={true} // 加這行
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => store._handleCloseDataEntrySetUnitModal()}
          variant="contained"
          sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
        >
          {t("Close")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          sx={{ textTransform: "none" }}
        >
          {isSubmitting ? t("Save...") : t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default DataEntrySetUnitModal;
